import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, Plus, Edit, Key, Search, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, UserPlus, Shield, AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiError } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // V26: Grace state alert
  const [graceState, setGraceState] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'analyst'
  });

  useEffect(() => {
    loadUsers();
    loadGraceState();
  }, [page, roleFilter, statusFilter]);

  const loadGraceState = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/super-admin/companies/current/grace-state`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGraceState(response.data?.graceState || null);
    } catch {
      // Grace state endpoint may not be available for current role
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pageSize,
        skip: page * pageSize
      });
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${BACKEND_URL}/api/super-admin/company/users?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams(formData);
      const response = await axios.post(`${BACKEND_URL}/api/super-admin/company/users?${params}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(response.data.message || 'User created successfully');
      setShowCreateModal(false);
      setFormData({ email: '', name: '', role: 'analyst' });
      loadUsers();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to create user'));
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams();
      if (formData.name) params.append('name', formData.name);
      if (formData.role) params.append('role', formData.role);
      if (formData.status) params.append('status', formData.status);
      
      await axios.put(`${BACKEND_URL}/api/super-admin/company/users/${selectedUser.userId}?${params}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update user'));
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('Reset password to default? The user will need to change it on next login.')) return;
    try {
      const response = await axios.post(`${BACKEND_URL}/api/super-admin/company/users/${userId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`Password reset to: ${response.data.defaultPassword}`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${BACKEND_URL}/api/super-admin/company/users/${user.userId}?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      loadUsers();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update user status'));
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      role: user.role || 'analyst',
      status: user.status || 'active'
    });
    setShowEditModal(true);
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(total / pageSize);

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
      case 'company_admin': return 'bg-blue-100 text-blue-800';
      case 'manager': return 'bg-green-100 text-green-800';
      case 'analyst': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {/* V26: Grace Period Alert */}
      {graceState && graceState.status === 'ACTIVE' && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4" data-testid="grace-period-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-800 dark:text-red-300">User Limit Exceeded — Grace Period Active</h3>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                The user limit was reduced from <strong>{graceState.previousLimit}</strong> to <strong>{graceState.newLimit}</strong>.
                Currently <strong>{graceState.currentActive ?? graceState.activeCountAtChange}</strong> active users.
                You need to deactivate <strong>{graceState.currentRequiredReduction ?? graceState.requiredReduction}</strong> user(s) before{' '}
                <strong>{new Date(graceState.graceDeadline).toLocaleDateString()}</strong>.
              </p>
              <p className="text-xs text-red-600 dark:text-red-500 mt-2">
                After the deadline, the system will automatically deactivate the least active non-admin users.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            <Users className="inline h-8 w-8 mr-2 text-green-600" />
            User Management
          </h1>
          <p className="text-slate-600 dark:text-slate-400">Manage users in your company</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="create-user-btn"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Add User
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-blue-600 mr-3" />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              As an Admin, you can create and manage <strong>Manager</strong> and <strong>Analyst</strong> users for your company.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
                data-testid="search-users-input"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
              data-testid="filter-role"
            >
              <option value="">All Roles</option>
              <option value="manager">Manager</option>
              <option value="analyst">Analyst</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
              data-testid="filter-status"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p className="text-slate-600 dark:text-slate-400">No users found</p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 bg-green-600 hover:bg-green-700 text-white"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Add Your First User
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead className="bg-slate-50 dark:bg-slate-800 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700 dark:text-slate-300">User</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700 dark:text-slate-300">Role</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700 dark:text-slate-300">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700 dark:text-slate-300">Created</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700 dark:text-slate-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {filteredUsers.map((user) => (
                      <tr key={user.userId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-slate-900 dark:text-white">{user.name || 'N/A'}</div>
                          <div className="text-xs text-slate-500">{user.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                            {user.role || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleToggleStatus(user)}
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 ${
                              user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {user.status === 'active' ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                            {user.status || 'active'}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <Button
                            onClick={() => openEditModal(user)}
                            variant="outline"
                            size="sm"
                            title="Edit User"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleResetPassword(user.userId)}
                            variant="outline"
                            size="sm"
                            className="text-yellow-600 border-yellow-600"
                            title="Reset Password"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, total)} of {total} users
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full bg-white dark:bg-slate-800 shadow-xl">
            <CardHeader>
              <CardTitle className="text-green-600 flex items-center">
                <UserPlus className="h-5 w-5 mr-2" />
                Add New User
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:border-slate-600"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:border-slate-600"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:border-slate-600"
                    required
                  >
                    <option value="manager">Manager</option>
                    <option value="analyst">Analyst</option>
                  </select>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    Default password: <strong>Welcome@123</strong>
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-300 mt-1">
                    User will be prompted to change on first login
                  </p>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">
                    Create User
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full bg-white dark:bg-slate-800 shadow-xl">
            <CardHeader>
              <CardTitle className="text-green-600">Edit User</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3">
                  <p className="text-sm text-slate-600 dark:text-slate-400">Email: {selectedUser.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:border-slate-600"
                  >
                    <option value="manager">Manager</option>
                    <option value="analyst">Analyst</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:border-slate-600"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button
                    type="button"
                    onClick={() => { setShowEditModal(false); setSelectedUser(null); }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">
                    Update User
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
